// src/components/admin/StaffEditor.jsx
import React, { useState, useEffect } from 'react';
import {
  collection,
  getDocs,
  doc,
  deleteDoc,
  addDoc,
  updateDoc,
  getDoc,
  query,
  where
} from 'firebase/firestore';
import { db } from '../../utils/firebase';
import { getSalonStaff, getSalonServices } from '../../api/salonApi'; // Import API functions
import { useSalonLocations } from '../../hooks/useSalonLocations';
import '../../styles/staffeditor.css';
import '../../styles/responsive-utils.css';
import '../../styles/staffeditor-responsive.css';

// Utility function to generate UUID
function generateUUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    var r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

function StaffEditor({ salonId, onUpdate, savedMessage, onPreview }) {
  const [staff, setStaff] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [isEditing, setIsEditing] = useState(false);
  const [editingStaffId, setEditingStaffId] = useState(null);

  // ── Multi-location ──────────────────────────────────────────────────────────
  const { locations } = useSalonLocations(salonId);
  const isMultiLocation = locations.length > 1;

  const [formData, setFormData] = useState({
    name: '',
    title: '',
    email: '',
    bio: '',
    photoURL: '',
    services: [],
    specialties: [],
    isActive: true,
    allowMultipleServices: false,
    locationIds:  [],   // array of locationIds this staff is assigned to
    locationId:   '',   // primary locationId (locationIds[0]) for backward compat
    locationName: '',   // primary location name
    schedule: {
      monday:    { available: true,  start: '09:00', end: '17:00' },
      tuesday:   { available: true,  start: '09:00', end: '17:00' },
      wednesday: { available: true,  start: '09:00', end: '17:00' },
      thursday:  { available: true,  start: '09:00', end: '17:00' },
      friday:    { available: true,  start: '09:00', end: '17:00' },
      saturday:  { available: true,  start: '10:00', end: '16:00' },
      sunday:    { available: false, start: '10:00', end: '16:00' },
    },
  });

  const [fieldErrors, setFieldErrors] = useState({
    name: '',
    services: ''
  });
  
  // Load staff using API function
  const loadStaff = async () => {
    try {
      console.log('Loading staff using API function...');

      // Use the API function instead of direct Firebase queries
      const staffData = await getSalonStaff(salonId);

      console.log('Loaded staff:', staffData);
      setStaff(staffData);
    } catch (error) {
      console.error('Error loading staff:', error);
      throw error;
    }
  };
  
  // Load services using API function - NO MORE FIREBASE QUERIES  
  const loadServices = async () => {
    try {
      console.log('Loading services using API function...');
      
      // Use the API function instead of direct Firebase queries
      const servicesData = await getSalonServices(salonId);
      
      // Filter for active services only
      const activeServices = servicesData.filter(service => service.isActive !== false);
      
      console.log('Loaded services:', activeServices);
      setServices(activeServices);
    } catch (error) {
      console.error('Error loading services:', error);
      throw error;
    }
  };
  
  // Load staff and services from Firebase
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError('');
        
        await Promise.all([loadStaff(), loadServices()]);
        
      } catch (err) {
        console.error('Error loading data:', err);
        setError('Failed to load staff and services. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    if (salonId) {
      loadData();
    }
  }, [salonId]);
  
  // Resolve service IDs (UUIDs stored in Firebase) to human-readable names.
  // Deduplicates by name so that a service copied to multiple locations (and
  // therefore stored as multiple IDs on the staff record) only appears once.
  const getStaffServiceNames = (staffServices) => {
    if (!staffServices || !Array.isArray(staffServices)) return 'No services assigned';
    const seen = new Set();
    const serviceNames = staffServices
      .map(serviceIdOrName => {
        const svc = services.find(s =>
          s.serviceId === serviceIdOrName ||
          s.id        === serviceIdOrName ||
          s.name      === serviceIdOrName
        );
        return svc ? svc.name : null;
      })
      .filter(name => {
        if (!name || seen.has(name)) return false;
        seen.add(name);
        return true;
      });

    return serviceNames.length > 0 ? serviceNames.join(', ') : 'No services assigned';
  };

  const validateField = (fieldName, value) => {
    let errorMsg = '';

    if (fieldName === 'name' && (!value || value.trim() === '')) {
      errorMsg = 'Staff Name is mandatory. Please fill in this field.';
    } else if (fieldName === 'services' && (!value || value.length === 0)) {
      errorMsg = 'At least one service must be selected.';
    }

    setFieldErrors(prev => ({ ...prev, [fieldName]: errorMsg }));
    return errorMsg === '';
  };

  const handleFieldBlur = (e) => {
    const { name, value } = e.target;
    if (name === 'name') {
      validateField(name, value);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === 'service') {
      const serviceId = value; // service UUID (serviceId or Firestore doc id)
      const updatedServices = [...formData.services];

      if (checked) {
        updatedServices.push(serviceId);
      } else {
        const index = updatedServices.indexOf(serviceId);
        if (index !== -1) {
          updatedServices.splice(index, 1);
        }
      }

      setFormData({
        ...formData,
        services: updatedServices
      });

      // Validate services field
      validateField('services', updatedServices);
    } else if (name.includes('schedule.')) {
      const [, day, field] = name.split('.');
      setFormData({
        ...formData,
        schedule: {
          ...formData.schedule,
          [day]: {
            ...formData.schedule[day],
            [field]: field === 'available' ? checked : value
          }
        }
      });
    } else {
      const newValue = type === 'checkbox' ? checked : value;
      setFormData({
        ...formData,
        [name]: newValue
      });

      // Validate name field on change
      if (name === 'name') {
        validateField(name, newValue);
      }
    }
  };
  
  // Derive primary locationId/locationName from an array of locationIds
  const resolvePrimary = (locationIds) => {
    if (!locationIds || locationIds.length === 0) return { locationId: '', locationName: '' };
    const primary = locations.find(l => l.locationId === locationIds[0]);
    return {
      locationId:   primary?.locationId   || locationIds[0],
      locationName: primary?.locationName || '',
    };
  };

  // ── Resolve checked service NAMES → stored service document IDs ──────────────
  // Services are now a global catalog (no locationId on service docs). For each
  // checked name, find the matching service doc and store its Firestore doc id.
  // Deduplicates so each service ID only appears once.
  const resolveServiceIdsFromNames = (checkedNames) => {
    const ids = [];
    checkedNames.forEach(name => {
      const matches = services.filter(s => s.name === name);
      matches.forEach(s => {
        const id = s.id; // always use Firestore doc id as the canonical key
        if (id && !ids.includes(id)) ids.push(id);
      });
    });
    return ids;
  };

  // ── Map stored service IDs (or legacy names) → unique name strings ────────────
  // Used when opening an existing staff record in the edit form so the checkboxes
  // reflect what is already saved, without duplicates.
  const resolveNamesFromServiceIds = (serviceIds) => {
    const names = new Set();
    (serviceIds || []).forEach(idOrName => {
      const svc = services.find(s =>
        s.serviceId === idOrName || s.id === idOrName || s.name === idOrName
      );
      if (svc) names.add(svc.name);
      // Unresolvable ID (e.g. service was deleted) — silently omit.
    });
    return Array.from(names);
  };

  const handleLocationToggle = (locationId, checked) => {
    setFormData(prev => {
      const updatedIds = checked
        ? [...prev.locationIds, locationId]
        : prev.locationIds.filter(id => id !== locationId);

      // Services are now a global catalog — not tied to locations.
      // No need to drop service selections when a location is removed.
      return {
        ...prev,
        locationIds: updatedIds,
        ...resolvePrimary(updatedIds),
      };
    });
  };

  const defaultSchedule = {
    monday:    { available: true,  start: '09:00', end: '17:00' },
    tuesday:   { available: true,  start: '09:00', end: '17:00' },
    wednesday: { available: true,  start: '09:00', end: '17:00' },
    thursday:  { available: true,  start: '09:00', end: '17:00' },
    friday:    { available: true,  start: '09:00', end: '17:00' },
    saturday:  { available: true,  start: '10:00', end: '16:00' },
    sunday:    { available: false, start: '10:00', end: '16:00' },
  };

  const handleAddStaff = () => {
    setIsEditing(true);
    setEditingStaffId(null);
    // Default new staff to first location (if any) for convenience
    const defaultLocationIds = locations.length > 0 ? [locations[0].locationId] : [];
    setFormData({
      name: '',
      title: '',
      bio: '',
      photoURL: '',
      services: [],
      specialties: [],
      isActive: true,
      allowMultipleServices: false,
      locationIds: defaultLocationIds,
      ...resolvePrimary(defaultLocationIds),
      schedule: { ...defaultSchedule },
    });
    setFieldErrors({ name: '', services: '' });
    setError('');
  };

  const handleEditStaff = (staff) => {
    setIsEditing(true);
    setEditingStaffId(staff.staffId || staff.id);
    // Support both legacy single locationId and new locationIds array
    const locationIds = staff.locationIds
      || (staff.locationId ? [staff.locationId] : []);
    setFormData({
      name:         staff.name         || '',
      title:        staff.title        || '',
      email:        staff.email        || '',
      bio:          staff.bio          || '',
      photoURL:     staff.photoURL     || '',
      // Convert stored service IDs → unique names so the deduplicated
      // checkboxes reflect the correct checked state without duplicates.
      services:     resolveNamesFromServiceIds(staff.services || []),
      specialties:  staff.specialties  || [],
      isActive:     staff.isActive !== false,
      allowMultipleServices: staff.allowMultipleServices || false,
      locationIds,
      ...resolvePrimary(locationIds),
      schedule:     staff.schedule     || { ...defaultSchedule },
    });
    setFieldErrors({ name: '', services: '' });
    setError('');
  };
  
  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditingStaffId(null);
  };
  
  // Link the salonUsers document to this staff record by writing staffId.
  // Called after every save so the Staff Portal can always resolve the correct ID.
  const linkSalonUser = async (staffId, email) => {
    if (!email) return;
    try {
      const snap = await getDocs(
        query(collection(db, 'salonUsers'), where('email', '==', email.trim().toLowerCase()))
      );
      snap.forEach(async d => {
        const data = d.data();
        if (data.salonId === salonId && data.role === 'staff' && data.staffId !== staffId) {
          await updateDoc(doc(db, 'salonUsers', d.id), { staffId });
        }
      });
    } catch {}
  };

  const handleSaveStaff = async () => {
    // Validate all mandatory fields
    const nameValid = validateField('name', formData.name);
    const servicesValid = validateField('services', formData.services);

    if (!nameValid || !servicesValid) {
      setError('Please fix the validation errors before saving.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const { locationId, locationName } = resolvePrimary(formData.locationIds);

      // formData.services holds checked service NAMES (deduplicated UI state).
      // Resolve them to the correct service document IDs for the staff member's
      // assigned locations before writing to Firebase (Option A).
      const resolvedServiceIds = resolveServiceIdsFromNames(formData.services);

      const staffData = {
        ...formData,
        // Store resolved IDs, not names — keeps the data model consistent and
        // ensures a stylist at Location A is NOT auto-linked to the same-named
        // service at Location B.
        services:     resolvedServiceIds,
        salonId:      salonId,
        locationIds:  formData.locationIds,
        locationId,
        locationName,
        updatedAt:    new Date(),
      };
      
      if (editingStaffId) {
        // Update existing staff
        const existingStaff = staff.find(s =>
          (s.staffId && s.staffId === editingStaffId) ||
          (s.id && s.id === editingStaffId)
        );

        if (existingStaff) {
          const docId = existingStaff.id;
          const canonicalStaffId = existingStaff.staffId || editingStaffId;
          await updateDoc(doc(db, 'staff', docId), staffData);

          // Sync salonUsers so the Staff Portal can resolve the correct ID
          await linkSalonUser(canonicalStaffId, formData.email);

          setStaff(staff.map(s =>
            (s.staffId === editingStaffId || s.id === editingStaffId)
              ? { ...staffData, id: docId, staffId: canonicalStaffId, leaveSummary: s.leaveSummary }
              : s
          ));
        }
      } else {
        // Add new staff
        const newStaffId = generateUUID();
        staffData.staffId = newStaffId;
        staffData.createdAt = new Date();

        const staffDocRef = await addDoc(collection(db, 'staff'), staffData);

        // Sync salonUsers by email so the Staff Portal works from the first login
        await linkSalonUser(newStaffId, formData.email);

        setStaff([...staff, {
          ...staffData,
          id: staffDocRef.id,
          leaveSummary: { annualLeaveUsed: 0, medicalLeaveUsed: 0, timeOffHours: 0, totalLeaveRecords: 0 }
        }]);
      }
      
      setIsEditing(false);
      setEditingStaffId(null);
      
      if (onUpdate) {
        onUpdate();
      }
    } catch (err) {
      console.error('Error saving staff:', err);
      setError('Failed to save staff. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleDeleteStaff = async (staffId, documentId) => {
    if (window.confirm('Are you sure you want to delete this staff? This will also delete all their leave records.')) {
      try {
        setLoading(true);
        
        // Delete from Firebase using document ID
        const staffDocRef = doc(db, 'staff', documentId);
        await deleteDoc(staffDocRef);
        
        // Also delete all leave records for this staff
        const leaveRef = collection(db, 'leave');
        const leaveQuery = query(
          leaveRef,
          where('staffId', '==', staffId),
          where('salonId', '==', salonId)
        );
        const leaveSnapshot = await getDocs(leaveQuery);
        
        const deletePromises = [];
        leaveSnapshot.forEach((leaveDoc) => {
          deletePromises.push(deleteDoc(doc(db, 'leave', leaveDoc.id)));
        });
        
        await Promise.all(deletePromises);
        
        // Update local state
        setStaff(staff.filter(staff => (staff.staffId || staff.id) !== staffId));
        
        if (onUpdate) {
          onUpdate();
        }
      } catch (err) {
        console.error('Error deleting staff:', err);
        setError('Failed to delete staff. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  };
  
  const handleToggleActive = async (staffId, documentId) => {
    try {
      const staff = staff.find(s => (s.staffId || s.id) === staffId);
      
      if (!staff) {
        return;
      }
      
      const updatedStaff = {
        ...staff,
        isActive: !staff.isActive
      };
      
      const staffDocRef = doc(db, 'staff', documentId);
      await updateDoc(staffDocRef, { isActive: updatedStaff.isActive });
      
      setStaff(staff.map(s => 
        (s.staffId || s.id) === staffId ? updatedStaff : s
      ));
      
      if (onUpdate) {
        onUpdate();
      }
    } catch (err) {
      console.error('Error toggling staff status:', err);
      setError('Failed to update staff status. Please try again.');
    }
  };

  if (loading && staff.length === 0) {
    return <div className="loading">Loading staff...</div>;
  }

  // ── Global service list for checkboxes ───────────────────────────────────────
  // Services are now a global catalog (not per-location). Deduplicate by name
  // so each service only appears as one checkbox regardless of how many docs
  // exist for it in Firestore during the transition period.
  const availableServiceNames = (() => {
    const seen = new Set();
    const result = [];
    services.forEach(svc => {
      if (seen.has(svc.name)) return;
      seen.add(svc.name);
      result.push(svc);
    });
    return result;
  })();

  const servicesContainerStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    width: '100%',
    marginTop: '10px'
  };

  const checkboxItemStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    width: 'auto',
    marginBottom: '8px',
    marginRight: '0',
    paddingLeft: '0'
  };

  const checkboxStyle = {
    marginRight: '8px',
    marginLeft: '0',
    flexShrink: 0,
    width: 'auto'
  };

  const labelStyle = {
    cursor: 'pointer',
    fontWeight: 'normal',
    fontSize: '14px',
    color: '#555',
    textAlign: 'left',
    margin: '0',
    paddingLeft: '0',
    display: 'inline-block',
    width: 'auto'
  };
  
  return (
    <div className="staff-editor">
      <div className="editor-page-header">
        <h2>Staff</h2>
        <div className="editor-page-actions">
          {savedMessage && <span className={`editor-save-msg${savedMessage.toLowerCase().includes('error') ? ' error' : ''}`}>{savedMessage}</span>}
          <button className="page-action-btn page-action-preview" type="button" onClick={onPreview}>Live Preview</button>
        </div>
      </div>
      
      {error && <div className="error-message">{error}</div>}
      
      {services.length === 0 && !isEditing && (
        <div className="no-services-warning">
          <p><strong>Notice:</strong> No active services found. Please add services in the Services tab before adding staff.</p>
        </div>
      )}
      
      {!isEditing ? (
        <>
          <div className="staff-list-actions">
            <button className="add-button" onClick={handleAddStaff}>
              Add New Staff
            </button>
          </div>
          
          {staff.length === 0 ? (
            <div className="no-staff">
              <p>No staff have been added yet. Click "Add New Staff" to get started.</p>
            </div>
          ) : (
            <div className="staff-list">
              <div className="staff-items">
                {staff.map(staff => (
                  <div 
                    key={staff.staffId || staff.id} 
                    className={`staff-item ${!staff.isActive ? 'inactive' : ''}`}
                  >
                    <div className="staff-content">
                      <div className="staff-header">
                        <div className="staff-photo">
                          {staff.photoURL ? (
                            <img
                              src={staff.photoURL}
                              alt={staff.name}
                              onError={e => { e.currentTarget.style.display = 'none'; e.currentTarget.nextSibling.style.display = 'flex'; }}
                            />
                          ) : null}
                          <div className="image-placeholder" style={staff.photoURL ? { display: 'none' } : undefined}>
                            {staff.name ? staff.name.charAt(0).toUpperCase() : '?'}
                          </div>
                        </div>
                        <div className="staff-info">
                          <h3 className="staff-name">
                            {staff.name}
                            {!staff.isActive && <span className="inactive-badge">Inactive</span>}
                          </h3>
                          {staff.title && <p className="staff-title">{staff.title}</p>}
                          {/* Location badges */}
                          {isMultiLocation && (
                            <div className="staff-location-badges">
                              {(staff.locationIds || (staff.locationId ? [staff.locationId] : [])).map(lid => {
                                const loc = locations.find(l => l.locationId === lid);
                                return loc ? (
                                  <span key={lid} className="staff-location-badge">{loc.locationName}</span>
                                ) : null;
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {staff.bio && <p className="staff-bio">{staff.bio}</p>}
                      
                      {staff.services && staff.services.length > 0 && (
                        <div className="staff-services">
                          <h5>Services:</h5>
                          <p>{getStaffServiceNames(staff.services)}</p>
                        </div>
                      )}
                      
                      
                      {!staff.isActive && (
                        <div className="staff-inactive">
                          This staff is currently inactive and not available for bookings.
                        </div>
                      )}
                    </div>
                    
                    <div className="staff-actions">
                      <button 
                        className="edit-button" 
                        onClick={() => handleEditStaff(staff)}
                      >
                        Edit
                      </button>
                      <button 
                        className={staff.isActive ? "remove-button" : "add-button"}
                        onClick={() => handleToggleActive(staff.staffId || staff.id, staff.id)}
                      >
                        {staff.isActive ? 'Deactivate' : 'Activate'}
                      </button>
                      <button 
                        className="remove-button"
                        onClick={() => handleDeleteStaff(staff.staffId || staff.id, staff.id)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="staff-form">
          <h3>{editingStaffId ? 'Edit Staff' : 'Add New Staff'}</h3>

              <div className="form-group">
                <label htmlFor="name">
                  Staff Name<span className="required-asterisk">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  onBlur={handleFieldBlur}
                  className={fieldErrors.name ? 'input-error' : ''}
                  required
                />
                {fieldErrors.name && <span className="field-error-message">{fieldErrors.name}</span>}
              </div>
              
              <div className="form-group">
                <label htmlFor="email">Login Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="e.g. jameson.gan@gmail.com"
                />
                <small style={{ color: '#888', fontSize: '12px' }}>
                  Must match the email used to create this staff member's Firebase Auth account.
                </small>
              </div>

              <div className="form-group">
                <label htmlFor="title">Title / Position</label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="e.g. Senior Staff, Colorist"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="photoURL">Photo URL</label>
                <input
                  type="text"
                  id="photoURL"
                  name="photoURL"
                  value={formData.photoURL}
                  onChange={handleInputChange}
                  placeholder="https://example.com/photo.jpg"
                />
                {formData.photoURL && (
                  <div className="photo-preview">
                    <img src={formData.photoURL} alt="Preview" />
                  </div>
                )}
              </div>
              
              <div className="form-group">
                <label htmlFor="bio">Bio</label>
                <textarea
                  id="bio"
                  name="bio"
                  value={formData.bio}
                  onChange={handleInputChange}
                  rows="4"
                  placeholder="Short description of the staff's experience and style..."
                />
              </div>
              
              {/* ── Location assignment (only shown for multi-location salons) ── */}
              {isMultiLocation && (
                <div className="form-group">
                  <label>Assigned Location(s)<span className="required-asterisk">*</span></label>
                  <div className="staff-location-rows">
                    {locations.map(loc => {
                      const isAssigned = formData.locationIds.includes(loc.locationId);
                      return (
                        <label
                          key={loc.locationId}
                          htmlFor={`loc-${loc.locationId}`}
                          className={`staff-location-row${isAssigned ? ' staff-location-row--active' : ''}`}
                        >
                          <input
                            type="checkbox"
                            id={`loc-${loc.locationId}`}
                            checked={isAssigned}
                            onChange={e => handleLocationToggle(loc.locationId, e.target.checked)}
                            className="staff-location-checkbox"
                          />
                          <span className="staff-location-name">{loc.locationName}</span>
                          {loc.isHeadOffice && (
                            <span className="staff-location-hq-badge">Head Office</span>
                          )}
                        </label>
                      );
                    })}
                  </div>
                  {formData.locationIds.length === 0 && (
                    <span className="field-error-message">Please assign this staff to at least one location.</span>
                  )}
                  <div className="form-helper-text">
                    <small>Select all locations where this staff member works.</small>
                  </div>
                </div>
              )}

              <div className="form-group">
                <label>
                  Services<span className="required-asterisk">*</span>
                </label>

                {/* Gate: multi-location salon with no location assigned yet */}
                {isMultiLocation && formData.locationIds.length === 0 ? (
                  <div className="no-services-message">
                    <p>Assign a location above first to see the services available at that location.</p>
                  </div>
                ) : services.length === 0 ? (
                  <div className="no-services-message">
                    <p>No active services available. Please add services in the Services tab first.</p>
                  </div>
                ) : availableServiceNames.length === 0 ? (
                  <div className="no-services-message">
                    <p>No services have been added for the selected location(s) yet. Add services in the Services tab first.</p>
                  </div>
                ) : (
                  /* One checkbox per unique service name.
                     formData.services stores names (not IDs) as intermediate UI state.
                     IDs are resolved from names only at save time via resolveServiceIdsFromNames. */
                  <div style={servicesContainerStyle}>
                    {availableServiceNames.map(service => (
                      <div key={service.name} style={checkboxItemStyle}>
                        <input
                          type="checkbox"
                          id={`service-${service.name}`}
                          name="service"
                          value={service.name}
                          checked={formData.services.includes(service.name)}
                          onChange={handleInputChange}
                          style={checkboxStyle}
                        />
                        <label htmlFor={`service-${service.name}`} style={labelStyle}>
                          {service.name}
                        </label>
                      </div>
                    ))}
                  </div>
                )}

                <div className="form-helper-text">
                  <small>Select the services this staff member can perform.</small>
                </div>
                {fieldErrors.services && <span className="field-error-message">{fieldErrors.services}</span>}
              </div>
              
              
              <div className="form-group checkbox-group">
                <label htmlFor="isActive">
                  Staff is active and available for booking
                </label>
                <input
                  type="checkbox"
                  id="isActive"
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-group checkbox-group">
                <label htmlFor="allowMultipleServices">
                  Allow this staff member to perform multiple services in the same time slot
                </label>
                <input
                  type="checkbox"
                  id="allowMultipleServices"
                  name="allowMultipleServices"
                  checked={formData.allowMultipleServices}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-actions">
                <button 
                  type="button" 
                  className="cancel-button"
                  onClick={handleCancelEdit}
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  className="update-button"
                  onClick={handleSaveStaff}
                  disabled={loading}
                >
                  {loading ? 'Saving...' : editingStaffId ? 'Update Staff' : 'Add Staff'}
                </button>
              </div>
        </div>
      )}
    </div>
  );
}

export default StaffEditor;